import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import api, { getApiError } from '../lib/axios';
import { Gavel, Loader2, ArrowRight, Sparkles } from 'lucide-react';

interface Form {
  title: string;
  description: string;
  target_keyword: string;
  target_url: string;
  budget_max_per_link: string;
  links_needed: string;
  min_dr: string;
  max_dr: string;
  min_traffic: string;
  country: string;
  language: string;
  listing_type: '' | 'guest_post' | 'niche_edit' | 'link_insertion';
  dofollow_required: boolean;
}

const EMPTY: Form = {
  title: '', description: '', target_keyword: '', target_url: '',
  budget_max_per_link: '', links_needed: '1',
  min_dr: '', max_dr: '', min_traffic: '',
  country: '', language: '',
  listing_type: '', dofollow_required: false,
};

export default function CampaignNew() {
  const nav = useNavigate();
  const [form, setForm] = useState<Form>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof Form>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const v = k === 'dofollow_required'
        ? (e.target as HTMLInputElement).checked
        : e.target.value;
      setForm(prev => ({ ...prev, [k]: v as Form[K] }));
    };

  const submit = async (publish: boolean) => {
    setError(null);

    const budget = parseFloat(form.budget_max_per_link);
    if (!form.title.trim()) return setError('Title is required');
    if (!Number.isFinite(budget) || budget <= 0) return setError('Budget must be a positive number');

    const links = Math.max(1, parseInt(form.links_needed, 10) || 1);

    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      target_keyword: form.target_keyword.trim() || undefined,
      target_url: form.target_url.trim() || undefined,
      budget_max_per_link: budget,
      links_needed: links,
      dofollow_required: form.dofollow_required,
    };
    if (form.min_dr) payload.min_dr = parseInt(form.min_dr, 10);
    if (form.max_dr) payload.max_dr = parseInt(form.max_dr, 10);
    if (form.min_traffic) payload.min_traffic = parseInt(form.min_traffic, 10);
    if (form.country.trim()) payload.country = form.country.trim().toUpperCase();
    if (form.language.trim()) payload.language = form.language.trim().toLowerCase();
    if (form.listing_type) payload.listing_type = form.listing_type;

    if (payload.min_dr != null && payload.max_dr != null && (payload.min_dr as number) > (payload.max_dr as number)) {
      return setError('Min DR cannot exceed Max DR');
    }

    setSubmitting(true);
    try {
      const res = await api.post('/api/campaigns', payload);
      const id = res.data.id as string;
      if (publish) {
        await api.post(`/api/campaigns/${id}/publish`);
      }
      nav(`/campaigns/${id}`);
    } catch (err) {
      setError(getApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <Gavel className="w-6 h-6 text-brand-600" /> New campaign
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Describe what you want. Sellers bid against each other. You pick the best one.
          </p>
        </header>

        <form onSubmit={e => { e.preventDefault(); submit(true); }} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6 shadow-card">
          <Section title="The brief">
            <Field label="Title" required>
              <input
                value={form.title}
                onChange={set('title')}
                maxLength={255}
                required
                placeholder="e.g. 10 tech blog guest posts for SaaS launch"
                className="input"
              />
            </Field>
            <Row>
              <Field label="Target keyword" hint="The keyword you want the link to help rank for.">
                <input value={form.target_keyword} onChange={set('target_keyword')} maxLength={200} placeholder="saas content marketing" className="input" />
              </Field>
              <Field label="Target URL" hint="The page on your site that the link should point to.">
                <input value={form.target_url} onChange={set('target_url')} maxLength={500} placeholder="https://example.com/blog/post" className="input" />
              </Field>
            </Row>
            <Field label="Description / notes" hint="Anything sellers should know — tone, topic angles, deadlines.">
              <textarea value={form.description} onChange={set('description')} maxLength={4000} rows={3} className="input resize-none" />
            </Field>
          </Section>

          <Section title="Budget & scale">
            <Row>
              <Field label="Max budget per link (USD)" required>
                <input type="number" min="1" step="1" value={form.budget_max_per_link} onChange={set('budget_max_per_link')} required placeholder="200" className="input" />
              </Field>
              <Field label="Links needed">
                <input type="number" min="1" max="500" value={form.links_needed} onChange={set('links_needed')} className="input" />
              </Field>
            </Row>
          </Section>

          <Section title="Quality requirements" hint="Leave blank for any.">
            <Row>
              <Field label="Min DR"><input type="number" min="0" max="100" value={form.min_dr} onChange={set('min_dr')} placeholder="40" className="input" /></Field>
              <Field label="Max DR"><input type="number" min="0" max="100" value={form.max_dr} onChange={set('max_dr')} placeholder="90" className="input" /></Field>
              <Field label="Min monthly traffic"><input type="number" min="0" value={form.min_traffic} onChange={set('min_traffic')} placeholder="5000" className="input" /></Field>
            </Row>
            <Row>
              <Field label="Country" hint="ISO alpha-2 code"><input value={form.country} onChange={set('country')} maxLength={5} placeholder="US" className="input" /></Field>
              <Field label="Language" hint="ISO code"><input value={form.language} onChange={set('language')} maxLength={10} placeholder="en" className="input" /></Field>
              <Field label="Link type">
                <select value={form.listing_type} onChange={set('listing_type')} className="input">
                  <option value="">Any</option>
                  <option value="guest_post">Guest post</option>
                  <option value="niche_edit">Niche edit</option>
                  <option value="link_insertion">Link insertion</option>
                </select>
              </Field>
            </Row>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.dofollow_required} onChange={set('dofollow_required')} className="w-4 h-4 rounded text-brand-600 focus:ring-brand-500" />
              Require <span className="font-mono font-semibold">dofollow</span> links only
            </label>
          </Section>

          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">{error}</div>
          )}

          <div className="pt-4 border-t border-slate-100 flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={submitting}
              className="px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl disabled:opacity-50"
            >
              Save as draft
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white font-semibold rounded-xl shadow-sm shadow-brand-500/25 disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Publish & start collecting bids <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
      <style>{`.input{width:100%;padding:.55rem .75rem;border:1px solid #e2e8f0;border-radius:.5rem;font-size:.875rem;outline:none;transition:border-color .15s}.input:focus{border-color:#0D9488;box-shadow:0 0 0 3px rgba(13,148,136,.15)}`}</style>
    </DashboardLayout>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{title}{hint ? <span className="ml-2 text-slate-400 normal-case font-normal">{hint}</span> : null}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      {hint && <span className="block text-xs text-slate-500 mb-1 mt-0.5">{hint}</span>}
      <div className={hint ? '' : 'mt-1'}>{children}</div>
    </label>
  );
}
